import * as React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { Alert, Text } from "@uikit";
import Modal from "@components/common/modal";
import useUser from "@hooks/useUser";
import { useApp } from "@store/app";

interface IProps {
  visible?: boolean;
}

const Currencies = ({ visible }: IProps) => {
  const display_currency = useApp(($) => $.display_currency);
  const setCurrency = useApp(($) => $.setCurrency);
  const hideModals = useApp(($) => $.hideModals);

  const { user, setDisplayCurrency } = useUser();

  const data = useStaticQuery(graphql`
    query {
      allGolfsaversSite {
        edges {
          node {
            config {
              default_currency
            }
          }
        }
      }
      allGolfsaversCurrencies(sort: { name: DESC }) {
        edges {
          node {
            name
            id
            symbol
            code
          }
        }
      }
    }
  `);

  let currencies = data.allGolfsaversCurrencies.edges.filter((edge) => {
    return edge.node.code !== "SAR";
  });

  let default_currency = React.useMemo(() => {
    return currencies.find(
      (edge: any) =>
        edge.node.code ===
        data.allGolfsaversSite.edges[0].node.config.default_currency
    ).node;
  }, [currencies, data]);

  React.useEffect(() => {
    if (display_currency.id !== "") return;
    let currency = currencies.find(
      (edge: any) => edge.node.code === default_currency.code
    );
    if (currency) {
      setCurrency(currency.node);
    }
  }, [default_currency, display_currency, currencies]);

  // sort currencies by name but have the current currency at the top
  currencies = currencies.sort((a: any, b: any) => {
    if (a.node.code === display_currency.code) return -1;
    if (b.node.code === display_currency.code) return 1;
    if (a.node.name < b.node.name) return -1;
    if (a.node.name > b.node.name) return 1;
    return 0;
  });

  const popular_currencies = process.env.GATSBY_TOP_CURRENCIES.split(",");

  const popularCurrencies = currencies
    ?.filter((edge: any) => {
      return popular_currencies.includes(edge.node.code);
    })
    .sort((a: any, b: any) => {
      return (
        popular_currencies.indexOf(a.node.code) -
        popular_currencies.indexOf(b.node.code)
      );
    });

  const otherCurrencies = currencies
    ?.filter((edge: any) => {
      return !popular_currencies.includes(edge.node.code);
    })
    .sort((a: any, b: any) => {
      return a.node.name.localeCompare(b.node.name);
    });

  const currencyButton = React.useMemo(() => {
    return (
      <Modal
        visible={visible}
        title="Price Display"
        fullScreenMobile
        hideCloseButton={false}
        maxWidth="850px"
        onClose={() => {
          hideModals();
        }}
      >
        <div className="py-2 px-4 sm:px-9 pt-6 !pb-2">
          <Alert
            type="warning"
            centered={false}
            title={`Please note that all bookings are billed in ${default_currency.code}.  Prices displayed in other currencies are estimated based on live exchange rates.`}
          />
        </div>

        <div className="flex flex-col gap-4 py-8  px-4 sm:px-9">
          <div className="flex flex-col items-start  w-full">
            <label className="block text-14 font-semibold text-black/60 mb-[6px] leading-[133%]">
              Popular Currencies
            </label>
            <div className="relative w-full grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-5  !pt-0 ">
              {popularCurrencies.map((edge: any) => {
                let selected = display_currency.code === edge.node.code;

                return (
                  <div
                    key={edge.node.id}
                    className={`cursor-pointer border rounded-lg p-3 hover:border-blue ${
                      selected ? "border-blue" : "border-transparent"
                    }`}
                    onClick={() => {
                      setCurrency(edge.node);
                      hideModals();
                      if (user !== null) {
                        setDisplayCurrency(edge.node.code);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      {" "}
                      <img
                        src={`https://golfsavers.b-cdn.net/assets/flags/currency_flags/${edge.node.code
                          .substring(0, 2)
                          .toLowerCase()}.svg`}
                        className="w-5 h-5 mr-2 rounded-full"
                        alt={edge.node.code}
                      />
                      <div>
                        <Text
                          size={13}
                          leading={1.3}
                          weight="bold"
                          color={`${selected ? "blue" : "black80"}`}
                          className="whitespace-nowrap"
                        >
                          {edge.node.name}
                        </Text>
                        <Text
                          size={12}
                          leading={1.5}
                          color={`${selected ? "blue" : "black60"}`}
                        >
                          {edge.node.code}{" "}
                        </Text>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col items-start  w-full">
            <label className="block text-14 font-semibold text-black/60 mb-[6px] leading-[133%]">
              Currencies
            </label>
            <div className="relative w-full grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-5  !pt-0 ">
              {otherCurrencies.map((edge: any) => {
                let selected = display_currency.code === edge.node.code;

                return (
                  <div
                    key={edge.node.id}
                    className={`cursor-pointer border rounded-lg p-3 hover:border-blue ${
                      selected ? "border-blue" : "border-transparent"
                    }`}
                    onClick={() => {
                      setCurrency(edge.node);
                      hideModals();
                      if (user !== null) {
                        setDisplayCurrency(edge.node.code);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <img
                        src={`https://golfsavers.b-cdn.net/assets/flags/currency_flags/${edge.node.code
                          .substring(0, 2)
                          .toLowerCase()}.svg`}
                        className="w-5 h-5 mr-2 rounded-full"
                        alt={edge.node.code}
                      />
                      <div>
                        <Text
                          size={13}
                          leading={1.3}
                          weight="bold"
                          color={`${selected ? "blue" : "black80"}`}
                          className="whitespace-nowrap"
                        >
                          {edge.node.name}
                        </Text>
                        <Text
                          size={12}
                          leading={1.5}
                          color={`${selected ? "blue" : "black60"}`}
                        >
                          {edge.node.code}{" "}
                        </Text>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    );
  }, [display_currency, default_currency, user, visible]);

  return <>{currencyButton}</>;
};

export default Currencies;
